import React, { useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import { X, Info, AlertTriangle, Sparkles } from 'lucide-react';

/**
 * AnnouncementBanner
 * Reads from global_state/announcement in Firestore.
 * When active, shows a dismissible banner at the top of the app.
 * Dismissal is stored in sessionStorage so it reappears on refresh.
 */
export default function AnnouncementBanner({ language = 'th', darkMode = false }) {
  const [announcement, setAnnouncement] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'global_state', 'announcement'), (snap) => {
      if (snap.exists() && snap.data().active) {
        setAnnouncement(snap.data());
        // Reset dismiss if content changed
        const stored = sessionStorage.getItem('announce_dismissed_id');
        if (stored !== snap.data().updatedAt) {
          setDismissed(false);
        }
      } else {
        setAnnouncement(null);
      }
    });
    return () => unsub();
  }, []);

  const handleDismiss = () => {
    setDismissed(true);
    if (announcement?.updatedAt) {
      sessionStorage.setItem('announce_dismissed_id', announcement.updatedAt);
    }
  };

  if (!announcement || dismissed) return null;

  const title = announcement.title || announcement.titleTH || announcement.titleEN || '';
  const message = announcement.message || announcement.messageTH || announcement.messageEN || '';

  if (!title && !message) return null;

  const typeStyles = {
    info: {
      bg: darkMode ? 'bg-blue-900/40 border-blue-700/50' : 'bg-blue-50 border-blue-200',
      icon: <Info size={16} className="text-blue-500 shrink-0" />,
      titleColor: darkMode ? 'text-blue-300' : 'text-blue-800',
      msgColor: darkMode ? 'text-blue-200/80' : 'text-blue-700',
    },
    warning: {
      bg: darkMode ? 'bg-amber-900/40 border-amber-700/50' : 'bg-amber-50 border-amber-200',
      icon: <AlertTriangle size={16} className="text-amber-500 shrink-0" />,
      titleColor: darkMode ? 'text-amber-300' : 'text-amber-800',
      msgColor: darkMode ? 'text-amber-200/80' : 'text-amber-700',
    },
    promo: {
      bg: darkMode ? 'bg-purple-900/40 border-purple-700/50' : 'bg-purple-50 border-purple-200',
      icon: <Sparkles size={16} className="text-purple-500 shrink-0" />,
      titleColor: darkMode ? 'text-purple-300' : 'text-purple-800',
      msgColor: darkMode ? 'text-purple-200/80' : 'text-purple-700',
    },
  };

  const style = typeStyles[announcement.type] || typeStyles.info;

  return (
    <div className={`mx-4 mt-2 mb-1 p-3 rounded-xl border flex items-start gap-2.5 animate-in slide-in-from-top-2 duration-300 ${style.bg}`}>
      {style.icon}
      <div className="flex-1 min-w-0">
        {title && <p className={`font-bold text-[13px] leading-tight ${style.titleColor}`}>{title}</p>}
        {message && <p className={`text-[11px] mt-0.5 leading-snug ${style.msgColor}`}>{message}</p>}
      </div>
      <button onClick={handleDismiss} className="p-0.5 opacity-50 hover:opacity-100 transition-opacity shrink-0">
        <X size={14} />
      </button>
    </div>
  );
}
